/**
 * Constantes da página Meus Anúncios
 * Configurações de tabs e status baseadas no design Figma
 */

const TAB_IDS = {
	ACTIVE: 'active',
	PENDING: 'pending',
	INACTIVE: 'inactive',
	INCOMPLETE: 'incomplete'
} as const;

const TAB_CONFIG = {
	[TAB_IDS.ACTIVE]: {
		id: TAB_IDS.ACTIVE,
		label: 'Ativos',
		statusFilter: 1
	},
	[TAB_IDS.PENDING]: {
		id: TAB_IDS.PENDING,
		label: 'Em análise',
		statusFilter: 2
	},
	[TAB_IDS.INACTIVE]: {
		id: TAB_IDS.INACTIVE,
		label: 'Inativos',
		statusFilter: 3
	},
	[TAB_IDS.INCOMPLETE]: {
		id: TAB_IDS.INCOMPLETE,
		label: 'Incompletos',
		statusFilter: 4
	}
} as const;

const TAB_ORDER = [
	TAB_IDS.ACTIVE,
	TAB_IDS.PENDING,
	TAB_IDS.INACTIVE,
	TAB_IDS.INCOMPLETE
] as const;

/**
 * Base do pageName dos eventos de analytics, em formato barra (igual garagem):
 * `/webmotors/garagem/meus-anuncios`. Com `tier: 3` gera
 * pageNameTier1=garagem, tier2=meus-anuncios, tier3=<aba>.
 */
const ANALYTICS_PAGE_BASE = '/webmotors/garagem/meus-anuncios';

/**
 * Slug de cada aba dentro do pageName/pageType dos eventos de analytics.
 * Espelha o padrão da garagem (`meus-anuncios-ativos`, etc.). Disparado tanto
 * no carregamento inicial quanto a cada clique de aba.
 */
const TAB_ANALYTICS_NAME = {
	[TAB_IDS.ACTIVE]: 'ativos',
	[TAB_IDS.PENDING]: 'em-analise',
	[TAB_IDS.INACTIVE]: 'inativos',
	[TAB_IDS.INCOMPLETE]: 'incompletos'
} as const;

const STATUS_IDS = {
	ACTIVE: 'active',
	PENDING: 'pending',
	INACTIVE: 'inactive',
	UNAVAILABLE: 'unavailable',
	TRANSACTION_FAILED: 'transaction_failed',
	IOS_PAYMENT_ONLY: 'ios_payment_only',
	INCOMPLETE: 'incomplete',
	PAYMENT_PENDING: 'payment_pending',
	PAYMENT_UNAUTHORIZED: 'payment_unauthorized'
} as const;

const STATUS_CONFIG = {
	[STATUS_IDS.ACTIVE]: {
		id: STATUS_IDS.ACTIVE,
		label: 'Anúncio ativo',
		tagColor: 'success',
		bgColor: '#e1f1e7',
		dotColor: '#22c55e',
		showMetrics: true,
		actions: ['markAsSold', 'boost'],
		menuActions: ['edit', 'view', 'deactivate'],
		menuDisabled: false
	},
	[STATUS_IDS.PENDING]: {
		id: STATUS_IDS.PENDING,
		label: 'Anúncio em análise',
		tagColor: 'info',
		bgColor: '#daf1f6',
		dotColor: '#06b6d4',
		showMetrics: false,
		actions: [],
		menuActions: ['edit'],
		menuDisabled: false
	},
	[STATUS_IDS.INACTIVE]: {
		id: STATUS_IDS.INACTIVE,
		label: 'Anúncio desativado',
		tagColor: 'disabled',
		bgColor: '#e9e9f5',
		dotColor: '#6a6977',
		showMetrics: true,
		actions: ['reactivate'],
		menuActions: [],
		menuDisabled: true
	},
	[STATUS_IDS.UNAVAILABLE]: {
		id: STATUS_IDS.UNAVAILABLE,
		label: 'Anúncio indisponível',
		tagColor: 'critical',
		bgColor: '#fde8ec',
		dotColor: '#e22446',
		showMetrics: false,
		actions: ['contactSupport'],
		menuActions: [],
		menuDisabled: true
	},
	[STATUS_IDS.TRANSACTION_FAILED]: {
		id: STATUS_IDS.TRANSACTION_FAILED,
		// PM/QA: a badge precisa orientar o usuário a pagar — reaproveita a
		// paleta warning ("A confirmar pagamento") ao invés de "Inativo".
		label: 'A confirmar pagamento',
		tagColor: 'warning',
		bgColor: '#fef3c7',
		dotColor: '#f59e0b',
		showMetrics: false,
		actions: ['pay'],
		menuActions: ['edit'],
		menuDisabled: false
	},
	[STATUS_IDS.IOS_PAYMENT_ONLY]: {
		id: STATUS_IDS.IOS_PAYMENT_ONLY,
		// Fluxo descontinuado pelo produto, mas mantemos mapeado por segurança
		// caso a API ainda devolva essa descrição.
		label: 'Pagamento via app iOS',
		tagColor: 'info',
		bgColor: '#daf1f6',
		dotColor: '#06b6d4',
		showMetrics: false,
		actions: [],
		menuActions: [],
		menuDisabled: true
	},
	[STATUS_IDS.INCOMPLETE]: {
		id: STATUS_IDS.INCOMPLETE,
		label: 'Anúncio incompleto',
		tagColor: 'warning',
		bgColor: '#fef3c7',
		dotColor: '#f59e0b',
		showMetrics: false,
		actions: ['complete'],
		menuActions: ['delete'],
		menuDisabled: false
	},
	[STATUS_IDS.PAYMENT_PENDING]: {
		id: STATUS_IDS.PAYMENT_PENDING,
		label: 'A confirmar pagamento',
		tagColor: 'warning',
		bgColor: '#fef3c7',
		dotColor: '#f59e0b',
		showMetrics: false,
		actions: [],
		menuActions: ['edit'],
		menuDisabled: false
	},
	[STATUS_IDS.PAYMENT_UNAUTHORIZED]: {
		id: STATUS_IDS.PAYMENT_UNAUTHORIZED,
		label: 'Pagamento não autorizado',
		tagColor: 'critical',
		bgColor: '#fde8ec',
		dotColor: '#e22446',
		showMetrics: false,
		actions: ['pay'],
		menuActions: ['edit'],
		menuDisabled: false
	}
} as const;

const ACTION_LABELS = {
	markAsSold: 'Já vendi',
	boost: 'Turbinar anúncio',
	reactivate: 'Reativar anúncio',
	complete: 'Completar anúncio',
	pay: 'Efetuar pagamento',
	contactSupport: 'Falar com atendimento'
} as const;

const MENU_IDS = {
	EDIT: 'edit',
	VIEW: 'view',
	DEACTIVATE: 'deactivate',
	MARK_AS_SOLD: 'markAsSold',
	DELETE: 'delete'
} as const;

const MENU_LABELS = {
	[MENU_IDS.EDIT]: 'Editar',
	[MENU_IDS.VIEW]: 'Ver anúncio',
	[MENU_IDS.DEACTIVATE]: 'Desativar anúncio',
	[MENU_IDS.MARK_AS_SOLD]: 'Já vendi',
	[MENU_IDS.DELETE]: 'Excluir'
} as const;

const MY_ADS_PAGE = {
	TITLE: 'Meus anúncios',
	CREATE_AD_BUTTON: 'Criar anúncio',
	EMPTY_STATE: {
		TITLE: 'Você ainda não tem anúncios',
		DESCRIPTION: 'Crie seu primeiro anúncio e comece a vender!'
	},
	ERROR_STATE: {
		TITLE: 'Erro ao carregar anúncios',
		DESCRIPTION: 'Não foi possível carregar seus anúncios. Tente novamente.',
		RETRY_BUTTON: 'Tentar novamente'
	},
	DEACTIVATE_MODAL: {
		TITLE: 'Tem certeza que deseja desativar o anúncio?',
		CONFIRM_LABEL: 'Desativar anúncio',
		DESCRIPTION: 'Após desativar, o anúncio não será mais exibido para os usuários.'
	},
	MARK_AS_SOLD_MODAL: {
		TITLE: 'Tem certeza que deseja marcar o anúncio como vendido?',
		CONFIRM_LABEL: 'Já vendi',
		DESCRIPTION: 'Ao confirmar, o anúncio será movido para inativos.'
	},
	DELETE_MODAL: {
		TITLE: 'Tem certeza que deseja excluir o anúncio?',
		CONFIRM_LABEL: 'Excluir',
		DESCRIPTION: 'Essa ação não poderá ser desfeita.'
	}
} as const;

export {
	TAB_IDS,
	TAB_CONFIG,
	TAB_ORDER,
	ANALYTICS_PAGE_BASE,
	TAB_ANALYTICS_NAME,
	STATUS_IDS,
	STATUS_CONFIG,
	ACTION_LABELS,
	MENU_IDS,
	MENU_LABELS,
	MY_ADS_PAGE
};

